import { calendarDateToLocal } from '../../../utils/calendarDate';
import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, ClipboardList, Timer, UserCheck, Users } from 'lucide-react';
import { MyControlsList } from './MyControlsList.tsx';
import { DueThisWeekStrip } from './DueThisWeekStrip.tsx';
import { MyProgressDonut } from './MyProgressDonut.tsx';
import { MyExceptionsList } from './MyExceptionsList.tsx';
import { EvidenceChecklistGrid } from './EvidenceChecklistGrid.tsx';
import { RerunsRequiredAlert } from './RerunsRequiredAlert.tsx';
import { TesterAvgTimeCard } from './TesterAvgTimeCard.tsx';
import { PageHeader, SectionHeader } from '../controller/shared.tsx';
import {
  useTesterAvgTestTime,
  useTesterMyControls,
  useTesterMyExceptions,
  useTesterTesters,
} from '../../../hooks/analytics/useTesterReports.ts';
import { AnalyticsSlicerBar, type SlicerDef } from '../AnalyticsSlicerBar.tsx';
import { AnalyticsTabs } from '../AnalyticsTabs.tsx';
import { KpiRow, type KpiItem } from '../KpiRow.tsx';
import { applySlicers, deriveSlicerOptions, monthBucket, monthLabel, useSlicerState } from '../slicerUtils.ts';

const SLICER_KEYS = ['period', 'workflow_status', 'frequency'];

/**
 * Tester "my work" analytics dashboard (Feature 5.3, reports #30-#36 minus
 * docx #35, an existing detail page). Every report is already server-scoped
 * to the caller (test_runs.assignee_user_id), so this page never accepts a
 * user-id param.
 *
 * IA: PageHeader -> slicer bar -> always-visible KPI row -> tabbed body. The
 * slicer bar and KPI row are driven off My Assigned Controls (report #30);
 * "Testing Period" is bucketed from `testing_period_start` (this row has no
 * `cycle` column) alongside Workflow Status / Frequency. The resulting
 * control-id set is threaded into the other per-control widgets as
 * `controlIdFilter`. My Progress (aggregated by status, no control_id) and
 * the Evidence Checklist (keyed by a picked run id, not the assignee) don't
 * share a joinable column and are left independent — noted in the digest.
 */
export const TesterDashboard: React.FC = () => {
  // "Tester" slicer (feedback round 2026-08) — lets a manager pick a
  // specific tester (from the distinct-assignees list) and re-scopes every
  // Tester report on the page via `?assignee_user_id=`. `''` means "my own
  // assignments" (server default, unchanged from before this round).
  const { data: testerOptions } = useTesterTesters();
  const [selectedAssignee, setSelectedAssignee] = useState<string>('');
  const assigneeUserId = selectedAssignee ? Number(selectedAssignee) : undefined;

  const { data: myControlsData } = useTesterMyControls(assigneeUserId);
  const myControlsRows = useMemo(() => myControlsData ?? [], [myControlsData]);
  const { data: myExceptionsData } = useTesterMyExceptions(assigneeUserId);
  const { data: avgTimeData } = useTesterAvgTestTime();

  const slicers = useSlicerState(SLICER_KEYS);

  const slicerDefs = useMemo<SlicerDef[]>(
    () => [
      {
        key: 'period',
        label: 'Testing Period',
        options: deriveSlicerOptions(myControlsRows, (r) => monthBucket(r.testing_period_start), monthLabel),
      },
      {
        key: 'workflow_status',
        label: 'Status',
        options: deriveSlicerOptions(myControlsRows, (r) => r.workflow_status),
      },
      { key: 'frequency', label: 'Frequency', options: deriveSlicerOptions(myControlsRows, (r) => r.frequency) },
    ],
    [myControlsRows]
  );

  const filteredRows = useMemo(
    () =>
      applySlicers(myControlsRows, slicers.values, {
        period: (r) => monthBucket(r.testing_period_start),
        workflow_status: (r) => r.workflow_status,
        frequency: (r) => r.frequency,
      }),
    [myControlsRows, slicers.values]
  );

  const controlIdFilter = useMemo(() => new Set(filteredRows.map((r) => r.control_id)), [filteredRows]);

  const kpis = useMemo<KpiItem[]>(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekOut = new Date(today);
    weekOut.setDate(weekOut.getDate() + 7);

    const overdue = filteredRows.filter((r) => r.due_date && (calendarDateToLocal(r.due_date) ?? today) < today).length;
    const dueThisWeek = filteredRows.filter((r) => {
      if (!r.due_date) return false;
      const due = calendarDateToLocal(r.due_date);
      return !!due && due >= today && due < weekOut;
    }).length;
    const completed = filteredRows.filter((r) => r.workflow_status === 'completed').length;
    const myExceptions = (myExceptionsData ?? []).filter((r) => controlIdFilter.has(r.control_id)).length;

    return [
      {
        key: 'assigned',
        title: 'My Assigned',
        value: filteredRows.length,
        icon: <ClipboardList className="h-5 w-5" />,
        color: 'bg-blue-100 text-blue-600',
      },
      {
        key: 'due_this_week',
        title: 'Due This Week',
        value: dueThisWeek,
        icon: <Clock className="h-5 w-5" />,
        color: 'bg-amber-100 text-amber-600',
      },
      {
        key: 'overdue',
        title: 'Overdue',
        value: overdue,
        icon: <AlertTriangle className="h-5 w-5" />,
        color: 'bg-rose-100 text-rose-600',
      },
      {
        key: 'exceptions',
        title: 'My Exceptions',
        value: myExceptions,
        icon: <AlertTriangle className="h-5 w-5" />,
        color: 'bg-orange-100 text-orange-600',
      },
      {
        key: 'completed',
        title: 'Completed',
        value: completed,
        icon: <CheckCircle2 className="h-5 w-5" />,
        color: 'bg-emerald-100 text-emerald-600',
      },
      {
        key: 'avg_time',
        title: 'Avg. Test Time',
        value: avgTimeData?.overall_avg_days != null ? `${avgTimeData.overall_avg_days.toFixed(1)}d` : '—',
        icon: <Timer className="h-5 w-5" />,
        color: 'bg-indigo-100 text-indigo-600',
        subtitle: 'org-wide',
      },
    ];
  }, [filteredRows, myExceptionsData, controlIdFilter, avgTimeData]);

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          icon={<UserCheck className="h-5 w-5" />}
          iconClassName="bg-blue-50 text-blue-600"
          title="Tester Dashboard"
          description="Your assigned tests, soonest due first"
          toolbar={
            (testerOptions?.length ?? 0) > 0 ? (
              <>
                <Users className="h-3.5 w-3.5 text-slate-400" aria-hidden />
                <label htmlFor="tester-slicer" className="text-sm font-medium text-slate-600">
                  Tester
                </label>
                <select
                  id="tester-slicer"
                  value={selectedAssignee}
                  onChange={(e) => setSelectedAssignee(e.target.value)}
                  className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                >
                  <option value="">My assignments</option>
                  {(testerOptions ?? []).map((t) => (
                    <option key={t.user_id} value={t.user_id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </>
            ) : undefined
          }
        />

        <AnalyticsSlicerBar
          slicers={slicerDefs}
          values={slicers.values}
          onChange={slicers.setValue}
          onClear={slicers.clear}
          activeCount={slicers.activeCount}
        />

        <KpiRow items={kpis} />

        <AnalyticsTabs
          tabs={[
            {
              key: 'overview',
              label: 'Overview',
              content: (
                <div className="grid grid-cols-1 gap-6">
                  <DueThisWeekStrip controlIdFilter={controlIdFilter} assigneeUserId={assigneeUserId} />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <MyProgressDonut assigneeUserId={assigneeUserId} />
                    <RerunsRequiredAlert controlIdFilter={controlIdFilter} assigneeUserId={assigneeUserId} />
                  </div>
                  <TesterAvgTimeCard />
                </div>
              ),
            },
            {
              key: 'analysis',
              label: 'Analysis',
              content: (
                <div className="grid grid-cols-1 gap-6">
                  <MyExceptionsList controlIdFilter={controlIdFilter} assigneeUserId={assigneeUserId} />
                  <EvidenceChecklistGrid />
                </div>
              ),
            },
            {
              key: 'raw',
              label: 'Raw Data',
              content: (
                <section>
                  <SectionHeader title="My Assigned Controls" />
                  <MyControlsList rows={filteredRows} />
                </section>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default TesterDashboard;
